import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { type SalesData } from '@courses/types-layer/types/checkout'
import { type CompletedLessons } from '@courses/types-layer/types/learner'
import { type LibraryProduct, type LibraryProducts } from '@courses/types-layer/types/library'
import { type UnlockModules } from '@courses/types-layer/types/module'
import { type SequenceNextIncompleteLesson } from '@courses/types-layer/types/sequence'

import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

import { LearnerApiService } from '../services/learner-api.service'

export interface LearnerApiQueryStoreOptions {
  /** When false, unlocked-modules is not fetched (catalog / preview). Default true. */
  fetchUnlockedModules?: MaybeRefOrGetter<boolean>
  /** When false, learner completed-lessons is not fetched. Default true. */
  fetchCompletedLessons?: MaybeRefOrGetter<boolean>
  /** When false, next-incomplete-lesson is not fetched. Default true. */
  fetchNextIncompleteLesson?: MaybeRefOrGetter<boolean>
}

export const useLearnerApiQueryStore = (
  courseId: MaybeRefOrGetter<string>,
  courseLocationId: MaybeRefOrGetter<string>,
  options?: LearnerApiQueryStoreOptions
) => {
  const progressEnabled = (getter: keyof LearnerApiQueryStoreOptions) => {
    return computed(() => {
      const key = options?.[getter]
      return key !== undefined ? toValue(key) : true
    })
  }
  const unlockedEnabled = progressEnabled('fetchUnlockedModules')
  const completedLearnerEnabled = progressEnabled('fetchCompletedLessons')
  const nextLessonEnabled = progressEnabled('fetchNextIncompleteLesson')
  const service = new LearnerApiService()
  const queryClient = useQueryClient()

  const queryKeys = {
    all: (locId?: string, cId?: string) => ['learner-api', locId, cId] as const,
    nextIncompleteLesson: (locId?: string, pId?: string, lId?: string) =>
      ['learner-api', locId, 'next-incomplete-lesson', pId, lId] as const,
    unlockedModules: (locId?: string, pId?: string) => ['learner-api', locId, 'unlocked-modules', pId] as const,
    completedLessons: (locId?: string, pId?: string) => ['learner-api', locId, 'completed-lessons', pId] as const,
    salesData: (locId?: string, pId?: string, oId?: string, src?: string) =>
      [...['learner-api', locId, 'sales-data', pId], ...(oId ? [oId] : []), ...(src ? [src] : [])] as const,
  }

  const fetchNextIncompleteLessonQuery = (lessonId?: MaybeRefOrGetter<string>) => {
    return useQuery<SequenceNextIncompleteLesson | any, Error>({
      queryKey: computed(() => queryKeys.nextIncompleteLesson(toValue(courseLocationId), toValue(courseId), toValue(lessonId))),
      queryFn: async (): Promise<SequenceNextIncompleteLesson | any> =>
        await service.getNextInCompleteLesson({
          courseId: toValue(courseId),
          courseLocationId: toValue(courseLocationId),
          lessonId: toValue(lessonId),
        }),
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => nextLessonEnabled.value && !!toValue(courseLocationId) && !!toValue(courseId)),
    })
  }

  const fetchUnlockedModulesQuery = () => {
    return useQuery<UnlockModules, Error>({
      queryKey: computed(() => queryKeys.unlockedModules(toValue(courseLocationId), toValue(courseId))),
      queryFn: async (): Promise<UnlockModules> =>
        await service.getUnlockedModules({
          courseId: toValue(courseId),
          courseLocationId: toValue(courseLocationId),
        }),
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => unlockedEnabled.value && !!toValue(courseLocationId) && !!toValue(courseId)),
    })
  }

  const fetchCompletedLessonsQuery = () => {
    return useQuery<CompletedLessons, Error>({
      queryKey: computed(() => queryKeys.completedLessons(toValue(courseLocationId), toValue(courseId))),
      queryFn: async (): Promise<CompletedLessons> =>
        await service.getCompletedLessons({
          courseId: toValue(courseId),
          courseLocationId: toValue(courseLocationId),
        }),
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => completedLearnerEnabled.value && !!toValue(courseLocationId) && !!toValue(courseId)),
    })
  }

  const fetchSalesDataQuery = (offerId?: MaybeRefOrGetter<string>, source?: MaybeRefOrGetter<string>) => {
    return useQuery<SalesData, Error>({
      queryKey: computed(() =>
        queryKeys.salesData(toValue(courseLocationId), toValue(courseId), toValue(offerId) || undefined, toValue(source) || undefined)
      ),
      queryFn: async (): Promise<SalesData> =>
        await service.getSalesData({
          courseId: toValue(courseId),
          courseLocationId: toValue(courseLocationId),
          offerId: toValue(offerId) || undefined,
          source: toValue(source) || undefined,
        }),
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => !!toValue(courseId)),
    })
  }

  const createNextIncompleteLessonForAllCoursesMutation = () => {
    return useMutation<Record<string, object>, Error, MaybeRefOrGetter<LibraryProducts>>({
      mutationFn: async (products: MaybeRefOrGetter<LibraryProducts>) => {
        const courseIdToUserId = toValue(products).reduce((acc: Record<string, string>, product: LibraryProduct) => {
          acc[product.id] = product.membershipUserId
          return acc
        }, {})
        return await service.getNextInCompleteForAllCourses(courseIdToUserId)
      },
    })
  }

  const fetchNextIncompleteLessonOnce = (
    targetCourseId: string,
    targetLocationId: string,
    lessonId?: string
  ): Promise<SequenceNextIncompleteLesson> => {
    return queryClient.fetchQuery({
      queryKey: queryKeys.nextIncompleteLesson(targetLocationId, targetCourseId, lessonId),
      queryFn: async (): Promise<SequenceNextIncompleteLesson> =>
        (await service.getNextInCompleteLesson({
          courseId: targetCourseId,
          courseLocationId: targetLocationId,
          lessonId,
        })) as SequenceNextIncompleteLesson,
    })
  }

  return {
    queryKeys,
    fetchNextIncompleteLessonOnce,
    fetchNextIncompleteLessonQuery,
    fetchUnlockedModulesQuery,
    fetchCompletedLessonsQuery,
    fetchSalesDataQuery,
    createNextIncompleteLessonForAllCoursesMutation,
  }
}
